import type { FC } from 'react';

import { useCms, useContent, useImageMapper, useSetting } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Badge, ButtonLink, Heading, Image, Stack, Text } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import type { TextImageRebrandedContent } from './text-image-rebranded-content.types';

/**
 * `TextImageRebranded` block for the CMS-agnostic lane, serving **both** providers. Text on one side,
 * image on the other, with `imagePosition` deciding which.
 *
 * NOTE (schema gap): `buttonLink`/`link` carry no display text in the migration bundle, so neither
 * button can be labelled and both are omitted — the same call `TitleTextCTA.tsx` and `HeroCard.tsx`
 * make, for the same reason. `uspList` resolves to `migrationUnresolvedReference` stubs with nothing
 * renderable on them.
 */
const TextImageRebranded: FC = () => {
  const { adapter } = useCms();
  const content = useContent<TextImageRebrandedContent>();
  const mapImage = useImageMapper();

  const headingLevel = (useSetting('headingLevel') || 'h2') as HeadingProps['as'];
  const objectFit = useSetting('objectFit') === 'contain' ? 'contain' : 'cover';
  const imageFirst = useSetting('imagePosition') === 'left';

  const image = mapImage(content.image);
  const richText = adapter.toRichText(content.content);
  const cta = adapter.toLink(content.buttonLink) ?? adapter.toLink(content.link);

  const imageBlock = image?.src ? (
    <Stack.Item grow>
      <Image src={image.src} alt={image.alt ?? ''} width="100%" objectFit={objectFit} aspectRatio={image.aspectRatio} />
    </Stack.Item>
  ) : null;

  const textBlock = (
    <Stack.Item grow>
      <Stack gap="6">
        {content.badgeText && (
          <Stack.Item>
            <Badge size="M">{content.badgeText}</Badge>
          </Stack.Item>
        )}
        {content.title && (
          <Heading as={headingLevel} size={{ initial: 'S', md: 'M' }}>
            {content.title}
          </Heading>
        )}
        {richText && (
          <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
            <RichText html={richText} />
          </Text>
        )}
        {cta?.text && (
          <Stack.Item grow={false}>
            <ButtonLink href={cta.href} target={cta.target}>
              {cta.text}
            </ButtonLink>
          </Stack.Item>
        )}
      </Stack>
    </Stack.Item>
  );

  return (
    <Stack direction={{ initial: 'column', md: 'row' }} gap={{ initial: '6', md: '12' }} alignY="center">
      {imageFirst ? imageBlock : null}
      {textBlock}
      {imageFirst ? null : imageBlock}
    </Stack>
  );
};

export default TextImageRebranded;
